<template>
  <div
    class="al-modal_content pl-scrollbar-mini has-scroll"
    :class="[{ 'is-open': openModal }, 'is-' + configModal.size]"
  >
    <div class="al-container-modal" :class="{ 'full-height': configModal.fullHeight }">
      <div :id="id" class="al-modal">
        <div class="al-modal_header">
          <div v-if="configModal.closeButton" class="al-modal_close">
            <al-button
              is-secondary-gray
              is-only-icon
              is-ghost
              size="sm"
              class="al-modal-close-button"
              icon="cross"
              @click="closeModal"
            />
          </div>
          <slot name="header">
            <div v-if="configModal.header" class="al-modal_title">{{ configModal.header }}</div>
          </slot>
        </div>
        <div v-if="$slots.default" class="al-modal_body pl-scrollbar-mini has-scroll">
          <slot></slot>
        </div>
        <div class="al-modal_footer">
          <slot name="footer">
            <div v-if="configModal.buttons" class="al-buttons">
              <al-button
                v-for="(button, buttonIndex) in configModal.buttons"
                :id="`modal-button-${buttonIndex}`"
                :key="buttonIndex"
                :text="button.label"
                :class="button.class"
                :variant="button.variant"
                :title="button.tooltip"
                :tooltip="button.tooltip"
                :disabled="button.disabled"
                @click="button.command"
              />
            </div>
          </slot>
        </div>
      </div>
    </div>

    <div class="al-modal_overlay" @click="closeModal"></div>
  </div>
</template>

<script>
import AlButton from '../../core/form-elements/buttons/AlButton.vue';

export default {
  name: 'AlModal',
  components: {
    AlButton
  },
  props: {
    openModal: {
      type: Boolean,
      default: false
    },
    config: {
      type: Object,
      default() {
        return {
          header: '',
          body: '',
          buttons: [],
          size: 'md',
          closeButton: true
        };
      }
    }
  },
  computed: {
    id() {
      // eslint-disable-next-line no-underscore-dangle
      return 'modal-'.concat(this.config.id ? this.config.id : this._uid);
    },
    configModal() {
      return {
        header: this.config.header ? this.config.header : '',
        body: this.config.body ? this.config.body : '',
        buttons: this.config.buttons ? this.config.buttons : [],
        size: this.config.size ? this.config.size : 'md',
        closeButton: this.config.closeButton ? this.config.closeButton : true
      };
    }
  },
  watch: {
    openModal(newState) {
      if (newState) {
        this.freezeBodyPage();
      } else {
        this.closeModal();
      }
    }
  },
  beforeDestroy() {
    this.unfreezeBodyPage();
  },
  mounted() {
    document.addEventListener('keydown', this.closeOnScape);
  },
  methods: {
    closeModal() {
      this.unfreezeBodyPage();
      this.$emit('close');
    },
    closeOnScape(e) {
      if (e.keyCode === 27) {
        this.closeModal();
      }
    },
    freezeBodyPage() {
      document.querySelector('html').classList.add('al-open-modal');
    },
    unfreezeBodyPage() {
      this.$nextTick(() => {
        document.querySelector('html').classList.remove('al-open-modal');
      });
    }
  }
};
</script>

<style lang="scss">
$br-default: 4px;

.al-modal_content {
  display: flex;
  height: 100%;
  left: 0;
  opacity: 0;

  overflow-y: auto;

  padding: 20px 15px;
  position: fixed;
  top: 0;

  transition: 0.3s;

  visibility: hidden;
  width: 100%;
  z-index: 1041;

  &.in,
  &.is-open {
    opacity: 1;
    transition: 0.3s ease 0.5s;
    visibility: visible;

    .al-modal {
      transform: scale(1);
      transition: transform 0.3s ease 0.5s;
    }

    .al-modal_overlay {
      opacity: 1;
      visibility: visible;
    }
  }

  &.is-full {
    .al-modal {
      border-radius: 0;
      height: 100%;
      max-width: 100%;
    }
  }

  &.is-lg {
    .al-modal {
      max-width: 1280px;
    }
  }

  &.is-md {
    .al-modal {
      max-width: 800px;
    }
  }

  &.is-sm {
    .al-modal {
      max-width: 480px;
    }
  }

  .al-modal_footer {
    .al-buttons {
      text-align: right;

      .al-button {
        margin: 0 4px;

        &:first-child {
          margin-left: 0;
        }

        &:last-child {
          margin-right: 0;
        }
      }
    }
  }
}

.al-modal_icon-help {
  vertical-align: middle;
}

.fade-enter-active,
.fade-leave-active {
  transition: 0.3s !important;
}

.fade-leave,
.fade-enter-to {
  opacity: 1 !important;
}

.fade-enter,
.fade-leave-to {
  opacity: 0 !important;
}

.fade-leave,
.fade-enter-to {
  .al-modal {
    transform: scale(1) !important;
    transition: transform 0.3s !important;
  }
}

.fade-enter,
.fade-leave-to {
  .al-modal {
    transform: scale(0.9) !important;
    transition: transform 0.3s !important;
  }
}

.al-container-modal {
  align-items: center;
  display: flex;
  height: 100%;
  left: 0;
  margin: auto;
  position: absolute;
  top: 0;
  width: 100%;

  &.full-height {
    min-height: 500px;
  }
}

.al-modal {
  align-self: safe center;
  background-color: white;
  border: 1px solid color(white, darken, 2);
  border-radius: $br-default;
  box-shadow: 0 3px 6px 0 rgba(0, 52, 35, 0.08);

  display: block;

  display: flex;
  flex-direction: column;

  flex-grow: 1;

  margin: auto;
  max-height: calc(100vh - 160px);
  max-width: 480px;
  overflow: hidden;
  position: relative;

  transform: scale(0.9);
  transition: transform 0.3s;

  width: 100%;
  z-index: 9999;
}

.al-modal_close {
  position: absolute;
  right: spacing(5);
  top: 50%;
  transform: translateY(-50%);
}

.al-modal_header,
.al-modal_footer {
  padding: spacing(6) spacing(7);
}

.al-modal_header,
.al-modal_body,
.al-modal_footer {
  .al-container,
  .al-container-fluid {
    padding: 0;
  }
}

.al-modal_header {
  border-bottom: 1px solid color(white, darken, 2);
  position: relative;

  .al-modal_title {
    color: color(black);
    font-size: font-size(base);
    font-weight: bold;
  }
}

.al-modal-max-width {
  max-width: calc(100% - 60px);
}

.al-modal_subtitle {
  color: color(black);
  font-size: font-size(s);
  font-weight: bold;
}

.al-modal_body {
  flex-grow: 1;
  overflow-x: hidden;
  overflow-y: auto;
  padding: spacing(6) spacing(7) (spacing(10) - 8px);

  .share-list-container {
    min-height: 250px;
  }

  .alert-message {
    &.alert-message-notice {
      word-break: break-word;
    }
  }
}

.al-modal_footer {
  border-top: 1px solid color(white, darken, 2);

  display: flex;
  flex-direction: column;

  .al-modal_confirm-actions {
    display: flex;
    justify-content: flex-end;
  }
}

.al-modal_overlay {
  background-color: rgba(color(black), 0.9);
  height: 100%;
  left: 0;
  opacity: 0;

  position: fixed;
  top: 0;

  transition: $transition_default;

  visibility: hidden;
  width: 100%;
  z-index: 1;
}

.al-modal-child-min-height {
  min-height: 350px;
}

html,
body {
  &.modal-open,
  &.al-open-modal {
    overflow: hidden !important;
    padding-right: 15px;
  }
}

.reset-al-modal {
  &.in {
    opacity: 1;
    visibility: visible;

    .al-modal {
      transform: scale(1);
    }

    .al-modal_overlay {
      opacity: 1;
      visibility: visible;
    }
  }

  .al-modal {
    .plvx-form {
      margin-left: initial;
      margin-right: initial;
    }

    .list-container {
      height: auto;
      overflow: hidden;
    }
  }
}

.modal-backdrop.fade {
  background-color: rgba(color(black), 0.9) !important;
  opacity: 0 !important;
  visibility: hidden;

  &.in {
    opacity: 10 !important;
    visibility: visible;
  }
}

.al-body_buttons {
  display: flex;
  justify-content: flex-end;
}
</style>
